// The sidebar's nav structure — the single list of what is in the menu, where
// it points, and which group it lives in (restructure 2026-09-17).
//
// ⚠️ SEPARATE FROM Sidebar.tsx ON PURPOSE. This module imports only icons and
// the pure nav types, never firebase or component code, so the structure can be
// asserted from a NODE-environment test. The two checks that matter most —
// "every menu entry points at a real route" and "every live page has a menu
// entry" — need to read this list programmatically, and importing the Sidebar
// component to get at it would drag in the firebase client and force a jsdom
// file the suite does not have headroom for.
//
// ── WHY THE GROUPS LOOK LIKE THIS ──
// The menu had grown to 32 items across 9 groups. The root cause was the
// ORGANISING PRINCIPLE, not the count: it was grouped by WHAT BUILT each
// feature ("Insights" held Meta ad reporting next to Google Ads next to five ops
// pages) rather than by what the user is doing. Every placement was defensible
// alone; nobody had looked at the whole.
//
// So: grouped by job, reporting sits next to the thing it reports on, and ops is
// out of the AM's path entirely (Operational is admin-only as a whole). A
// non-admin sees 24 items in 6 groups; an admin sees 33.
//
// ⚠️ Sub-headers are LABELS, not collapsible groups — see sidebarNav.ts.
// ⚠️ Sentence case throughout, including sub-headers (brand rule 11). The menu
// used to mix "Meta Setup" / "HTML Hosting" with "Lead bot" / "Source health".

import {
  Activity,
  AtSign,
  BarChart3,
  Bot,
  ScrollText,
  Waypoints,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarRange,
  ClipboardList,
  DatabaseBackup,
  Eye,
  FileCode2,
  FileText,
  Library,
  Images,
  Lightbulb,
  FlaskConical,
  Gauge,
  Hash,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  Phone,
  PenSquare,
  PieChart,
  Plug,
  PlugZap,
  Receipt,
  Rocket,
  Search,
  Send,
  Settings,
  Share2,
  SlidersHorizontal,
  Target,
  Users,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type {
  SidebarNavItem,
  SidebarNavSectionOf,
  SidebarNavSubheader,
} from '../../lib/sidebarNav';

export type NavItemDef = SidebarNavItem & { icon: LucideIcon };
export type NavEntryDef = NavItemDef | SidebarNavSubheader;
export type NavSectionDef = SidebarNavSectionOf<NavEntryDef>;

/**
 * Pinned above the groups. Dashboard belongs to no section — it is where you
 * land, not part of a job — so `resolveActiveGroup('/')` is deliberately null
 * and nothing auto-expands there.
 */
export const DASHBOARD_ITEM: NavItemDef = {
  to: '/',
  label: 'Dashboard',
  icon: LayoutDashboard,
  end: true,
};

/**
 * Pinned below the groups.
 *
 * ⚠️⚠️ `end: true` IS REQUIRED AND IS NEW. Xero moved into the menu at
 * /settings/xero, and without `end` this item prefix-matches it and BOTH light
 * up. Nothing lived under /settings before, which is why it had no `end`.
 */
export const SETTINGS_ITEM: NavItemDef = {
  to: '/settings',
  label: 'Settings',
  icon: Settings,
  end: true,
};

export const NAV_SECTIONS: NavSectionDef[] = [
  {
    label: 'Clients',
    entries: [
      { to: '/accounts', label: 'Accounts', icon: Building2 },
      { to: '/contacts', label: 'Contacts', icon: Users },
    ],
  },
  {
    label: 'Advertising',
    entries: [
      // The AMs' daily triage dashboard (alerts, pulse, the book). TOP of the
      // group: it is the first thing an AM checks.
      // The Fitness account managers' own dashboard (their home page); anyone can open theirs.
      { to: '/dashboard/am', label: 'AM dashboard', icon: LayoutDashboard },
      { to: '/advertising/home', label: 'Advertising home', icon: Gauge },
      // ⚠️ Had NO menu entry at all until this restructure — a live staff page
      // reachable only by typing the URL.
      { to: '/tools/client-report', label: 'Client report view', icon: Eye },
      { subheader: 'Meta ads' },
      // The Meta specialist team's daily board — FIRST under the sub-header,
      // for the same reason Advertising home leads the group: it is the first
      // thing that team checks. ⚠️ NO `end`, and none is wanted: React Router
      // matches whole segments, so /advertising/meta does not match
      // /advertising/meta-home (the /advertising/meta-setup precedent). Adding
      // one defensively here would be cargo-cult; adding one to "Ad creation"
      // would break /advertising/meta/compose/:draftId.
      { to: '/advertising/meta-home', label: 'Meta home', icon: Gauge },
      // ⚠️ NO `end` — it must stay lit across /advertising/meta/compose/:draftId.
      // Safe next to "Meta assets" because a prefix match needs a segment
      // boundary, so /advertising/meta-setup does not match /advertising/meta.
      { to: '/advertising/meta', label: 'Ad creation', icon: Target },
      // ⚠️⚠️ `end: true` IS LOAD-BEARING. Without it this swallows every
      // /reporting/* route — audience, google-ads, social and the five ops
      // pages — and lights on all of them.
      { to: '/reporting', label: 'Ad analysis', icon: BarChart3, end: true },
      // ⚠️ "Audience insights" (who RESPONDED), deliberately not "Ad audiences":
      // under one sub-header that would read as the same word as "Meta assets"
      // (the saved audiences you TARGET) meaning the opposite thing.
      { to: '/reporting/audience', label: 'Audience insights', icon: PieChart },
      // Per-account reusable Meta assets (lead forms, saved audiences).
      { to: '/advertising/meta-setup', label: 'Meta assets', icon: SlidersHorizontal },
      { subheader: 'Google ads' },
      // The Google specialist team's daily board — first under the sub-header,
      // mirroring "Meta home".
      { to: '/advertising/google-home', label: 'Google home', icon: Gauge },
      { to: '/reporting/google-ads', label: 'Google ad analysis', icon: Search },
    ],
  },
  {
    label: 'Organic social',
    entries: [
      { subheader: 'Publishing' },
      // ⚠️ `end` so Drafts / Create / Post to many do not also light Calendar.
      { to: '/publishing', label: 'Calendar', icon: CalendarClock, end: true },
      { to: '/publishing/drafts', label: 'Drafts', icon: FileText },
      { to: '/publishing/new', label: 'Create', icon: PenSquare },
      { to: '/publishing/multi', label: 'Post to many', icon: Users },
      { subheader: 'Community' },
      { to: '/community', label: 'Inbox', icon: Inbox },
      { subheader: 'Insights' },
      { to: '/reporting/social', label: 'Organic reports', icon: Share2 },
    ],
  },
  {
    // KYCKCONNECT (redesign Phase 1): everything about lead handling in ONE group. It gathers what used to be scattered:
    // the lead relay (was under Advertising), the lead bot, its reports and All leads (were under Tools -> AI bots), plus pages
    // with no menu entry at all (bot settings, Speed to Lead settings, the business profile). ⚠️ Admin-only items keep exactly
    // the visibility they had: Leads (was "All leads"), Lead relay routes, and the new cross-club Calls and Test lab.
    label: 'KyckConnect',
    entries: [
      // ⚠️ `end` so it does not light on every /kyckconnect/* page below.
      { to: '/kyckconnect', label: 'Overview', icon: Gauge, end: true },
      { to: '/kyckconnect/leads', label: 'Leads', icon: Target, adminOnly: true },
      { to: '/kyckconnect/approvals', label: 'Approvals', icon: Bot },
      { to: '/kyckconnect/calls', label: 'Calls', icon: Phone, adminOnly: true },
      // The log is for everyone (re-keying a failed lead is ordinary staff work); the routes are admin-only: a route decides
      // which dealership gets a client's leads. No `end` on routes: /kyckconnect/lead-relay/routes/:id SHOULD light it.
      { to: '/kyckconnect/lead-relay/log', label: 'Lead relay log', icon: ScrollText },
      { to: '/kyckconnect/lead-relay/routes', label: 'Lead relay routes', icon: Waypoints, adminOnly: true },
      { to: '/kyckconnect/numbers', label: 'Numbers and pools', icon: Hash },
      { to: '/kyckconnect/test-lab', label: 'Test lab', icon: FlaskConical, adminOnly: true },
      { to: '/kyckconnect/reports', label: 'Reports', icon: BarChart3 },
    ],
  },
  {
    label: 'Client outreach',
    entries: [
      { to: '/broadcasts', label: 'Broadcasts', icon: Send },
      { to: '/surveys', label: 'Surveys', icon: ListChecks },
    ],
  },
  {
    label: 'Franchise',
    entries: [
      { to: '/campaigns', label: 'Campaigns', icon: Megaphone },
      { to: '/plans', label: 'Plans', icon: ClipboardList },
      { to: '/rollouts', label: 'Rollouts', icon: Rocket },
    ],
  },
  {
    label: 'Tools',
    entries: [
      { to: '/tools/html-hosting', label: 'HTML hosting', icon: FileCode2 },
      // The STAFF list of client-portal tiles. Beside HTML hosting because that
      // is where a tile is published from ("Add to Kyck portal").
      { to: '/tools/portal-library', label: 'Portal library', icon: Library },
      // Client-portal photo and video uploads, by brand: where Kyck reviews them
      // (commit 13). The client-side 'Asset management' nav item is live for every portal user.
      { to: '/tools/portal-assets', label: 'Portal assets', icon: Images },
      // Photo and video ideas Kyck recommends to clients (view-only in the portal). Run I.
      { to: '/tools/inspiration', label: 'Inspiration', icon: Lightbulb },
      // ⚠️ "Booking calendars", NOT "Lead bot calendars". The calendar model is
      // its own module because the CRM and web chat will both want booking, so it
      // is a plain Tools item (it sat under an "AI bots" sub-header, which emptied
      // when the bot pages moved to KyckConnect).
      { to: '/booking-calendars', label: 'Booking calendars', icon: CalendarCheck },
    ],
  },
  {
    // ⚠️ ADMIN-ONLY AS A WHOLE. Taking ops out of the AM's path is most of what
    // this restructure buys; every item here was previously scattered through
    // Insights / Advertising / Workspace.
    label: 'Operational',
    adminOnly: true,
    entries: [
      // Zernio profile creation + the Meta connect wizard.
      { to: '/onboarding', label: 'Account onboarding', icon: Plug },
      // Register a GymSales club, watch its first pull, link its Kyck account.
      { to: '/crm-onboarding', label: 'CRM onboarding', icon: UserPlus },
      // Discover GA4 properties, match them to accounts, connect (no deploy).
      { to: '/ga4-connections', label: 'GA4 connections', icon: Plug },
      { to: '/connection-health', label: 'Connections', icon: PlugZap },
      // ⚠️ These five are an INTERIM. The coverage-page overhaul replaces
      // Coverage map / Pull control / Source health / Sync runs / Connections
      // with one exception-first page, and Operational drops to ~5 items when it
      // lands. Naming them plainly is what makes that obvious.
      { to: '/reporting/grid', label: 'Coverage map', icon: CalendarRange },
      { to: '/reporting/coverage', label: 'Pull control', icon: CalendarRange },
      { to: '/reporting/health', label: 'Source health', icon: CalendarRange },
      // ⚠️ The ROUTE is open to any signed-in staff (list + fetch) and only
      // Apply is admin-gated, server-side — but this group is admin-only as a
      // whole, so a non-admin reaches it by URL, not by menu. Scott placed it
      // here beside the other fleet worklists; revisit if AMs need it in the menu.
      { to: '/instagram-backfill', label: 'Instagram backfill', icon: AtSign },
      { to: '/reporting/sync-health', label: 'Sync runs', icon: Activity },
      { to: '/reporting/backfills', label: 'Backfills', icon: DatabaseBackup },
      { to: '/account-analytics', label: 'Account analytics', icon: PieChart },
      // ⚠️ Had NO menu entry at all until this restructure. Its two sub-pages
      // (/settings/xero/mapping, /settings/xero/allocations) are reached from
      // here, which is why this item carries no `end`.
      { to: '/settings/xero', label: 'Xero', icon: Receipt },
    ],
  },
];
